import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../services/api";
import type {
  Catalog,
  CatalogFilters,
  CatalogsResponse,
  CatalogResponse,
  FeaturedCatalogsResponse,
} from "../types/catalog";

// ==========================================
// 📋 HOOKS PARA LISTA DE CATÁLOGOS
// ==========================================

/**
 * Hook para obtener lista paginada de catálogos con filtros
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useCatalogs({
 *   active: true,
 *   featured: true,
 *   page: 1,
 *   limit: 10,
 * });
 * ```
 */
export function useCatalogs(filters?: CatalogFilters) {
  return useQuery({
    queryKey: ["catalogs", filters],
    queryFn: async (): Promise<CatalogsResponse> => {
      const params = new URLSearchParams();

      if (filters?.active !== undefined) params.append("active", filters.active.toString());
      if (filters?.featured !== undefined) params.append("featured", filters.featured.toString());
      if (filters?.name) params.append("name", filters.name);
      if (filters?.slug) params.append("slug", filters.slug);
      if (filters?.includeDeleted) params.append("includeDeleted", filters.includeDeleted.toString());
      if (filters?.createdAfter) params.append("createdAfter", filters.createdAfter);
      if (filters?.createdBefore) params.append("createdBefore", filters.createdBefore);
      if (filters?.createdBy) params.append("createdBy", filters.createdBy);
      if (filters?.sortField) params.append("sortField", filters.sortField);
      if (filters?.sortDirection) params.append("sortDirection", filters.sortDirection);
      if (filters?.page) params.append("page", filters.page.toString());
      if (filters?.limit) params.append("limit", filters.limit.toString());

      const response = await apiClient.get<CatalogsResponse>(
        `/catalog?${params.toString()}`
      );
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutos (los catálogos cambian poco)
    gcTime: 1000 * 60 * 10, // 10 minutos
  });
}

/**
 * Hook para obtener solo catálogos activos
 * 
 * @example
 * ```tsx
 * const { data } = useActiveCatalogs();
 * ```
 */
export function useActiveCatalogs() {
  return useQuery({
    queryKey: ["catalogs", "active"],
    queryFn: async (): Promise<CatalogsResponse> => {
      const response = await apiClient.get<CatalogsResponse>(
        "/catalog?active=true"
      );
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10, // 10 minutos
  });
}

/**
 * Hook para obtener catálogos destacados
 * 
 * @example
 * ```tsx
 * const { data } = useFeaturedCatalogs(5);
 * ```
 */
export function useFeaturedCatalogs(limit = 10) {
  return useQuery({
    queryKey: ["catalogs", "featured", limit],
    queryFn: async (): Promise<FeaturedCatalogsResponse> => {
      const params = new URLSearchParams();
      params.append("limit", limit.toString());

      const response = await apiClient.get<FeaturedCatalogsResponse>(
        `/catalog/featured?${params.toString()}`
      );
      return response;
    },
    staleTime: 1000 * 60 * 10, // 10 minutos (destacados cambian muy poco)
    gcTime: 1000 * 60 * 15, // 15 minutos
  });
}

// ==========================================
// 📄 HOOKS PARA CATÁLOGO INDIVIDUAL
// ==========================================

/**
 * Hook para obtener catálogo por ID
 * 
 * @example
 * ```tsx
 * const { data } = useCatalog("catalog-uuid");
 * ```
 */
export function useCatalog(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["catalog", id],
    queryFn: async (): Promise<Catalog> => {
      const response = await apiClient.get<CatalogResponse>(`/catalog/${id}`);
      return response.data;
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!id,
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10, // 10 minutos
  });
}

/**
 * Hook para obtener catálogo por slug (SEO-friendly)
 * 
 * @example
 * ```tsx
 * const { data } = useCatalogBySlug("ceramicas");
 * ```
 */
export function useCatalogBySlug(
  slug: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["catalog", "by-slug", slug],
    queryFn: async (): Promise<Catalog> => {
      const response = await apiClient.get<CatalogResponse>(
        `/catalog/slug/${slug}`
      );
      return response.data;
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!slug,
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10, // 10 minutos
  });
}

// ==========================================
// 🔄 HOOKS LEGACY (DEPRECADOS)
// ==========================================

/**
 * @deprecated Usar `useCatalog()` + `useCategories()` en su lugar
 * 
 * Este hook intentaba obtener un catálogo con sus categorías incluidas
 * usando ?include=categories, pero el nuevo backend NO soporta esto.
 * 
 * Ahora debes:
 * 1. Usar `useCatalog(id)` para obtener el catálogo
 * 2. Usar `useCategoriesByCatalog(catalogId)` para obtener las categorías
 * 
 * O usar React Query's `useQueries` para hacer ambas peticiones en paralelo.
 * 
 * @example
 * ```tsx
 * // ❌ ANTIGUO (deprecado)
 * const { data } = useCatalogWithCategories(catalogId);
 * 
 * // ✅ NUEVO (correcto)
 * const { data: catalog } = useCatalog(catalogId);
 * const { data: categories } = useCategoriesByCatalog(catalogId);
 * 
 * // O en paralelo con useQueries:
 * const [catalogQuery, categoriesQuery] = useQueries({
 *   queries: [
 *     { queryKey: ["catalog", catalogId], queryFn: ... },
 *     { queryKey: ["categories", "catalog", catalogId], queryFn: ... }
 *   ]
 * });
 * ```
 */
export function useCatalogWithCategories(id: string) {

  return useQuery({
    queryKey: ["catalog", id, "with-categories-DEPRECATED"],
    queryFn: async () => {
      throw new Error(
        "El endpoint /catalog/:id?include=categories ya no existe. " +
        "Usa useCatalog(id) + useCategoriesByCatalog(catalogId) en su lugar. " +
        "Las categorías ahora se obtienen por separado en /categories/catalog/:catalogId"
      );
    },
    enabled: false, // Deshabilitar este hook
  });
}
